import { useGetHeliosBrowseItemsQuery, useGetHeliosDataTablesQuery } from 'store/services/helios/dataHelios';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography as MuiTypography, Popover, styled } from '@mui/material';

import { Loader } from 'components/common/Loader';
import { BrowseItem } from './BrowseItem';
import type { HeliosRecordDataTableColumnSettings } from 'types/dataHelios';
import type { HeliosCellData } from 'types/helios/agendaListing';
import { ComparisonOperator, ConditionValueSource, HeliosRecordSystemDatasource, LogicalOperator } from 'types/dataHelios';
import { computeDisplayColumnsText } from 'utils/helios/displayColumns';

const BrowseWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
  gap: '12px',
  backgroundColor: theme.palette.background.default,
  height: '100%',
  overflowX: 'auto',
}));

const browseCellBaseStyles = {
  display: 'block',
  padding: '8px 12px',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
} as const;

const browseCellInteractiveStyles = {
  ...browseCellBaseStyles,
  cursor: 'pointer',
  '&:hover': {
    borderColor: 'primary.main',
    '& .MuiTypography-root': {
      textDecoration: 'underline',
    },
  },
} as const;

interface Props {
  value: string;
  dataType?: string;
  settings?: HeliosRecordDataTableColumnSettings;
  templateItemId?: number;
  templateViewId?: string;
  data?: HeliosCellData;
}

const BrowseBase = ({ value, settings, templateItemId, templateViewId, data }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const isEmptyValue = value === '';
  const isSingleBrowse = !settings?.browse?.dynamicRelationId;
  const browseCount = isSingleBrowse ? (isEmptyValue ? 0 : 1) : (data?.dynamicRelationValuesCount ?? 0);
  const displayColumns = settings?.browse?.displayColumns;
  const codeListTemplateId = settings?.browse?.codeListTemplateId;

  const { data: browseItems, isFetching } = useGetHeliosBrowseItemsQuery(
    {
      templateId: templateViewId ?? '',
      templateItemId: templateItemId?.toString() ?? '',
      recordId: data?.recordNumber.toString() ?? '',
    },
    { skip: !open || isSingleBrowse || browseCount === 0 }
  );

  const rawValueId = isSingleBrowse && data?.rawValue && typeof data.rawValue === 'object' && 'id' in data.rawValue ? data.rawValue.id : undefined;

  const precomputedDisplayText =
    isSingleBrowse && data?.rawValue && typeof data.rawValue === 'object' && 'displayText' in data.rawValue && data.rawValue.displayText
      ? String(data.rawValue.displayText)
      : null;

  const singleBrowseArgs = useMemo(
    () => ({
      templateId: codeListTemplateId ?? '',
      filters: rawValueId
        ? [
            {
              leftBracket: false,
              rightBracket: false,
              datasource: {
                templateDataItemId: 0,
                systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
              },
              leftLogicalOperator: LogicalOperator.And,
              comparisonOperator: ComparisonOperator.Equals,
              value: {
                conditionValueSource: ConditionValueSource.None,
                staticValue: String(rawValueId || ''),
                systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
                templateDataItemId: 0,
              },
            },
          ]
        : [],
      formData: null,
      searchKeyword: '',
      offset: 0,
      limit: 1,
      orderBy: '',
      orderDesc: false,
      useServiceAccount: false,
    }),
    [codeListTemplateId, rawValueId]
  );

  const { data: selectedRecordData } = useGetHeliosDataTablesQuery(singleBrowseArgs, {
    skip: !codeListTemplateId || !isSingleBrowse || browseCount === 0 || !displayColumns || displayColumns.length === 0 || !!precomputedDisplayText,
  });

  const multiBrowseArgs = useMemo(
    () => ({
      templateId: codeListTemplateId ?? '',
      filters: [],
      formData: null,
      searchKeyword: '',
      offset: 0,
      limit: 100,
      orderBy: '',
      orderDesc: false,
      useServiceAccount: false,
      masterTemplate: {
        templateId: templateViewId ?? '',
        recordId: data?.recordNumber.toString() ?? '',
      },
    }),
    [codeListTemplateId, data?.recordNumber, templateViewId]
  );

  const { data: multiRecordData, isFetching: isFetchingMultiRecord } = useGetHeliosDataTablesQuery(multiBrowseArgs, {
    skip: !codeListTemplateId || isSingleBrowse || browseCount === 0 || !displayColumns || displayColumns.length === 0 || !open,
  });

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const browseContent = useCallback(() => {
    if (browseCount === 0) return <MuiTypography />;
    if (isFetching) return <Loader />;
    return browseItems?.map((item, index) => (
      <BrowseItem
        key={item.recordNumber + index}
        id={item.recordNumber}
        name={item.name ?? ''}
        detailTemplateId={settings?.browse?.detailTemplateId ?? ''}
      />
    ));
  }, [browseCount, browseItems, isFetching, settings?.browse?.detailTemplateId]);

  if (isSingleBrowse) {
    if (browseCount === 0) return <MuiTypography />;
    if (data?.rawValue && typeof data?.rawValue === 'object') {
      const displayText = precomputedDisplayText
        ? precomputedDisplayText
        : displayColumns && displayColumns.length > 0 && selectedRecordData?.data && selectedRecordData.data.length > 0
          ? computeDisplayColumnsText(selectedRecordData, displayColumns) || data.rawValue?.name || ''
          : data.rawValue?.name || '';

      const detailTemplateId = settings?.browse?.detailTemplateId;

      if (detailTemplateId && data.rawValue.id) {
        return (
          <Box sx={browseCellInteractiveStyles}>
            <BrowseItem id={data.rawValue.id} name={displayText || '—'} detailTemplateId={detailTemplateId} />
          </Box>
        );
      }

      return (
        <Box sx={browseCellBaseStyles}>
          <MuiTypography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayText || '—'}
          </MuiTypography>
        </Box>
      );
    }
  }

  return (
    <Box onClick={handleClick} sx={browseCellInteractiveStyles}>
      <MuiTypography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {t('common.count')}: {browseCount}
      </MuiTypography>
      <Popover
        open={open}
        disableScrollLock
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClick={(e) => e.stopPropagation()}>
        <BrowseWrapper>
          {isFetchingMultiRecord || isFetching ? (
            <Loader />
          ) : displayColumns && displayColumns.length > 0 && multiRecordData?.data && multiRecordData.data.length > 0 ? (
            multiRecordData.data
              .filter((record) => browseItems?.some((item) => item.recordNumber === record.recordNumber))
              .map((record, index) => {
                const browseItem = browseItems?.find((item) => item.recordNumber === record.recordNumber);
                const displayText = computeDisplayColumnsText({ data: [record], columns: multiRecordData.columns }, displayColumns);
                return (
                  <BrowseItem
                    key={record.recordNumber + index}
                    id={record.recordNumber}
                    name={displayText || browseItem?.name || String(record.recordNumber)}
                    detailTemplateId={settings?.browse?.detailTemplateId ?? ''}
                  />
                );
              })
          ) : (
            browseContent()
          )}
        </BrowseWrapper>
      </Popover>
    </Box>
  );
};
export const BrowseHelios = memo(BrowseBase) as typeof BrowseBase;
